import { jwtDecode } from 'jwt-decode';
import LogRocket from 'logrocket';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useBetween } from 'use-between';
import { buildApiUrl } from '../apis/AbstractApi';
import { AuthLib } from '../contexts/AuthContext/AuthLib';
import { S3GRole } from '../generated-types/authRoles';

/**
 * Get the authentication library
 * @returns The authentication library
 */
const useAuthLib = (): AuthLib => {
  /**
   * Clear the current authentication state
   */
  const getClearedAuthState = useCallback(
    (): AuthState => ({
      Authenticated: false,
      ExpireDate: undefined,
      UserRoles: [],
    }),
    []
  );

  /**
   * The current auth state
   */
  const [authState, setAuthState] = useState<AuthState>(getClearedAuthState());

  /**
   * The current token
   */
  const [token, setToken] = useState<string | null>(null);

  /**
   * Whether initialization occurs. This helps prevent a token's state from being
   *  interpreted before we can process it.
   */
  const [initialized, setInitialized] = useState(false);
  const refreshPromise = useRef<Promise<string | undefined> | null>(null);

  /**
   * Check if the current token is expired
   */
  const isExpired = useCallback(() => {
    return (
      authState.ExpireDate == undefined || new Date() > authState.ExpireDate
    );
  }, [authState.ExpireDate]);

  /**
   * Refresh the token
   */
  const refreshToken = useCallback(async (initialTry = false) => {
    if (refreshPromise.current) return refreshPromise.current;

    refreshPromise.current = (async () => {
      try {
        // Perform the actual refresh
        console.log('Refreshing token...');
        const response = await fetch(buildApiUrl('auth/refresh'), {
          method: 'POST',
          credentials: 'include', // Includes the refresh token
        });

        // Process if the refresh was successful
        if (!response.ok) {
          if (!initialTry) console.error('Token refresh was not successful.');
          else
            console.log(
              'No token initialization needed. Refresh token is not valid or missing.'
            );
          setToken(null);
          return;
        } else {
          if (!initialTry) console.log('Token refresh was successful.');
          else
            console.log(
              'Token initialization from refresh token was successful.'
            );
        }

        // Set the token
        const updatedToken = await response.text();
        setToken(updatedToken);
        return updatedToken;
      } finally {
        refreshPromise.current = null;
      }
    })();

    return refreshPromise.current;
  }, []);

  // This use effect is specifically for reacting off of token changes
  useEffect(() => {
    // If the token is not set, clear the auth state and end early
    if (token == null) {
      setAuthState(getClearedAuthState());
      return;
    }

    // Setup
    const parsedToken = jwtDecode<S3GToken>(token);
    const newAuthState = getClearedAuthState();

    // Set the expiration date
    const newExpirationDate = new Date(0);
    newExpirationDate.setUTCSeconds(parsedToken.exp);
    newAuthState.ExpireDate = newExpirationDate;

    // Set the roles
    if (Array.isArray(parsedToken.role)) {
      newAuthState.UserRoles = parsedToken.role.map(
        (r) => S3GRole[r as keyof typeof S3GRole]
      );
    } else {
      newAuthState.UserRoles = [
        S3GRole[parsedToken.role as keyof typeof S3GRole],
      ];
    }

    // Set the user as authenticated
    newAuthState.Authenticated = true;

    // Update the log rocket state. The email will have already been set during
    //  the signup or login
    LogRocket.identify(parsedToken.Id, {
      email: parsedToken.email,
      roles: JSON.stringify(parsedToken.role),
    });

    // Update the state
    setAuthState(newAuthState);
    setInitialized(true);

    // Set the timer
    const safetyMarginMs = 30 * 1000;
    const now = Date.now();
    const expirationTime = newExpirationDate.getTime();
    const delay = expirationTime - now - safetyMarginMs;
    const refreshDelay = delay > 0 ? delay : 0; // if already expired, refresh immediately

    console.log(
      `Scheduling token refresh to occur in ${Math.round(
        refreshDelay / 1000
      )} seconds.`
    );

    const timerId = setTimeout(() => {
      refreshToken();
    }, refreshDelay);

    // Clean up the timer if the token changes or component unmounts
    return () => {
      clearTimeout(timerId);
    };
  }, [getClearedAuthState, refreshToken, token]);

  /**
   * Logout the user. This will clear the token and reset the authentication state.
   */
  const logout = useCallback(() => {
    setToken(null);
  }, []);

  /**
   * Check to see if the user has the provided roles
   */
  const checkRoles = useCallback(
    (roles: S3GRole[]) => {
      // If the user has no roles assigned to them, return early
      if (authState.UserRoles.length == 0) return false;

      // See if the user has all the passed in roles
      let authorized = true;
      for (const role of roles) {
        authorized = authorized && authState.UserRoles.includes(role);

        // If authorized is changed to false, return early
        if (!authorized) return false;
      }

      return authorized;
    },
    [authState.UserRoles]
  );

  /**
   * Get a navigate route that is more important for the user based on the roles they have.
   */
  const getNavigateRouteBasedOnRole = useCallback(() => {
    if (checkRoles([S3GRole.Admin])) {
      return '/admin';
    } else if (checkRoles([S3GRole.ClientOnboarding])) {
      return '/client-onboarding';
    } else if (checkRoles([S3GRole.Manager])) {
      return '/management';
    } else if (checkRoles([S3GRole.User])) {
      return '/training';
    } else if (
      authState.UserRoles.length == 0 ||
      authState.UserRoles.every((u) => u == undefined)
    ) {
      return '/welcome';
    } else {
      return '/';
    }
  }, [authState.UserRoles, checkRoles]);

  const authLib = useMemo<AuthLib>(
    () => ({
      Initialized: initialized,
      AuthState: authState,
      Logout: logout,
      RefreshToken: refreshToken,
      SetToken: (t) => setToken(t),
      GetToken: () => token,
      IsExpired: isExpired,
      CheckRoles: checkRoles,
      GetNavigateRouteBasedOnRole: getNavigateRouteBasedOnRole,
    }),
    [
      authState,
      checkRoles,
      getNavigateRouteBasedOnRole,
      initialized,
      isExpired,
      logout,
      refreshToken,
      token,
    ]
  );

  return authLib;
};

const useSharedAuthLib = () => useBetween(useAuthLib);

export default useSharedAuthLib;
